"use client";

import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { MapPin } from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { DEFAULT_BRANCH_IMAGE } from "@/lib/branches/image";
import { useHub, useHubDensity } from "./hub-context";

/** 지도 위에 띄운 요소의 그림자 — 검색 필터 알약과 같은 값. */
export const FLOATING_SHADOW =
  "shadow-[0_1px_2px_rgba(20,18,12,0.10),0_4px_12px_rgba(20,18,12,0.10)]";

/**
 * 허브 목록의 좌우 여백 — **한 줄로 맞춘다**.
 * 모바일은 12px: 지도 위 부유 레이어(`left-3 right-3`)와 드로어 안이 같은 선을 써야
 * 드로어를 올릴 때 필터바·칩·헤더가 옆으로 흔들리지 않는다.
 * sm 이상은 0 — 바깥 컨테이너(태블릿 `p-6`, 데스크톱 목록 열 `px-6`)가 여백을 준다.
 */
export const HUB_GUTTER = "px-3 sm:px-0";

/**
 * 지점 행 썸네일 한 변(px) — 정사각. 모바일·태블릿 100 · 데스크톱 목록 열 2열(960–1279) 112 ·
 * 넓은 데스크톱(1280+) 148(09-29: 84 · 136 에서 키움).
 */
export const STATION_THUMB = 100;
export const STATION_THUMB_DESK = 112;
export const STATION_THUMB_XL = 148;

/**
 * 행 썸네일. 지점 행은 정사각 100(데스크톱 112 · 넓은 데스크톱 148 — `StationThumb`),
 * 차종 행은 136×102(데스크톱 2열 118×88 · 넓은 데스크톱 136×102), 지점 머리는 56.
 * 사진이 없으면 회색 면만 남긴다 — 장식 아이콘을 넣지 않는다.
 */
/**
 * object-cover 로 칸을 채울 때 받아야 할 원본 폭. 지점 사진은 대부분 3:2 가로라 정사각
 * 칸에 채우면 좌우가 잘리고 **높이**가 칸을 결정한다 — 칸 폭만큼만 받으면 높이가 모자라
 * 늘여 그려 흐려진다(09-29, hinata 750×498 → 256×170 을 112 정사각 2x 에 확대).
 */
const COVER_SOURCE_RATIO = 1.5;
function coverWidth(w: number, h: number): number {
  return Math.round(Math.max(w, h * COVER_SOURCE_RATIO));
}

export function Thumb({
  src,
  alt,
  w,
  h,
  desk,
  xl,
  rounded = "rounded-lg",
  bordered = true,
}: {
  src?: string | null;
  alt: string;
  w: number;
  h: number;
  /** 데스크톱 목록 열(mapShow, 960px+)에서 쓸 크기. 없으면 기본 크기 그대로. */
  desk?: { w: number; h: number };
  /**
   * 넓은 데스크톱(xl, 1280px+)에서 쓸 크기. 2열 목록 칸이 충분히 넓어지는 폭에서만
   * 사진을 키운다 — 지도 폭 하한(960px)에서는 칸이 좁아 글자 자리가 모자란다(09-23).
   */
  xl?: { w: number; h: number };
  rounded?: string;
  /**
   * 반투명 회색 테두리(안쪽 1px) — 기본 켜짐(09-28: 지점·장비 사진 모두). 흰 배경
   * 사진이 카드와 섞이지 않게 한다. 컨테이너의 inset ring 은 `fill` 이미지가 덮어
   * 로딩 전에만 보이므로, 이미지 **위에** 얹는 오버레이로 그린다.
   */
  bordered?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden bg-gray-100",
        // 크기는 CSS 변수로 — 인라인 width/height 는 반응형 클래스를 이긴다.
        "w-(--thumb-w) h-(--thumb-h) mapShow:w-(--thumb-w-desk) mapShow:h-(--thumb-h-desk) xl:w-(--thumb-w-xl) xl:h-(--thumb-h-xl)",
        rounded,
      )}
      style={
        {
          "--thumb-w": `${w}px`,
          "--thumb-h": `${h}px`,
          "--thumb-w-desk": `${desk?.w ?? w}px`,
          "--thumb-h-desk": `${desk?.h ?? h}px`,
          "--thumb-w-xl": `${xl?.w ?? desk?.w ?? w}px`,
          "--thumb-h-xl": `${xl?.h ?? desk?.h ?? h}px`,
        } as CSSProperties
      }
    >
      {src ? (
        <OptimizedImage
          src={src}
          alt={alt}
          fill
          sizes={[
            xl ? `(min-width: 1280px) ${coverWidth(xl.w, xl.h)}px` : null,
            desk ? `(min-width: 960px) ${coverWidth(desk.w, desk.h)}px` : null,
            `${coverWidth(w, h)}px`,
          ]
            .filter(Boolean)
            .join(", ")}
          className="object-cover"
        />
      ) : null}
      {bordered ? (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 ring-1 ring-inset ring-gray-900/10",
            rounded,
          )}
        />
      ) : null}
    </div>
  );
}

/**
 * 섹션 헤더 — 두 줄 블록. 1줄은 클래스명(자르지 않고 필요하면 줄바꿈),
 * 2줄은 면허·최저가·개수 같은 보조 문구. 좌우로 벌려 놓으면 긴 클래스명이
 * "Standard motorcycle (≤…" 처럼 잘리므로 양쪽 정렬을 쓰지 않는다.
 */
export function SectionHeader({
  title,
  meta,
  thin = false,
  className,
}: {
  title: string;
  meta?: string;
  /** 2열 그리드에서 두 칸을 가로지를 때 쓰는 추가 클래스(col-span-2). */
  className?: string;
  /**
   * 한 줄 얇은 헤더 — 지점의 차종 시트가 쓴다: "클래스 · 면허 문구" 를
   * text-xs 회색 한 줄로만. 두 줄 블록(카탈로그)은 기본값.
   */
  thin?: boolean;
}) {
  if (thin) {
    // 배기량 구분 밴드 — 행과 확실히 갈리도록 회색 면 위에
    // 왼쪽 클래스 알약(검정 테두리 = 필터 알약과 같은 어휘) + 오른쪽 면허 문구.
    return (
      <div
        className={cn(
          "flex items-center gap-3 bg-gray-50 py-2.5 mapShow:mt-3 mapShow:rounded-lg",
          HUB_GUTTER,
          "mapShow:px-3",
          className,
        )}
      >
        <span className="shrink-0 rounded-full border-2 border-gray-900 bg-white px-2.5 py-0.5 text-[12px] font-semibold text-gray-900 mapShow:px-3 mapShow:text-[13px]">
          {title}
        </span>
        {meta ? (
          <span className="min-w-0 truncate text-xs text-gray-500 mapShow:text-[13px]">{meta}</span>
        ) : null}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "bg-gray-50 py-2",
        HUB_GUTTER,
        "mapShow:bg-transparent mapShow:pt-5 mapShow:pb-2",
        className,
      )}
    >
      <p className="text-[13px] font-semibold text-gray-900 mapShow:text-sm">
        {title}
      </p>
      {meta ? (
        <p className="mt-0.5 text-[12px] text-gray-500 mapShow:text-[13px]">
          {meta}
        </p>
      ) : null}
    </div>
  );
}

/**
 * 세그먼트 탭 — 사이트 공용 shadcn Tabs(`components/ui/tabs`) 그대로.
 * muted 바탕 + 흰 활성 알약(검정 채움 없음). 지도 위에 띄울 때만 링·그림자를 더한다.
 */
export function SegmentedTabs<T extends string>({
  value,
  options,
  onChange,
  floating = false,
  fullWidth = false,
  triggerClassName,
  listClassName,
  className,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  /** 지도 위에 띄울 때 — 링 + 그림자 */
  floating?: boolean;
  /** 패널·시트 안에서 가로 전체를 채운다. */
  fullWidth?: boolean;
  /** 트리거(각 탭) 추가 클래스 — 크기·최소 폭 조정용 */
  triggerClassName?: string;
  /** 탭 바깥 틀(TabsList) 추가 클래스 — 모양 바꾸기용(허브 목록 머리의 회색 알약 토글) */
  listClassName?: string;
  className?: string;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={(next) => onChange(next as T)}
      className={cn(fullWidth ? "w-full" : "w-auto", className)}
    >
      <TabsList
        className={cn(
          fullWidth ? "w-full" : "w-auto",
          floating &&
            `pointer-events-auto ring-1 ring-black/10 ${FLOATING_SHADOW}`,
          listClassName,
        )}
      >
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            className={cn(fullWidth && "flex-1", triggerClassName)}
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * 알약 칩 한 개 — 선택 = **진한 테두리**(검정 채움 아님).
 * 검색 필터바의 활성 카테고리 칩(chipVariant="outline")과 같은 어휘다.
 */
export function Chip({
  label,
  selected,
  onClick,
  floating = false,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  floating?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "shrink-0 rounded-full px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition",
        // 데스크톱은 필터 줄과 같이 한 단계 크게(09-23)
        "mapShow:px-3.5 mapShow:py-2 mapShow:text-sm",
        selected
          ? "border-2 border-gray-900 bg-white text-gray-900"
          : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
        floating && `pointer-events-auto ${FLOATING_SHADOW}`
      )}
    >
      {label}
    </button>
  );
}

/**
 * 지점 행 사진 — 바이크·캠핑장 행과 스켈레톤이 같은 크기 규칙을 쓴다. 데스크톱 목록 열(wide)
 * 에서만 112 · 148 로 키운다(모바일 드로어·태블릿은 compact 라 늘 100).
 */
export function StationThumb({ src, alt }: { src?: string | null; alt: string }) {
  const wide = useHubDensity() === "wide";
  return (
    <Thumb
      src={src || DEFAULT_BRANCH_IMAGE}
      alt={alt}
      w={STATION_THUMB}
      h={STATION_THUMB}
      desk={wide ? { w: STATION_THUMB_DESK, h: STATION_THUMB_DESK } : undefined}
      xl={wide ? { w: STATION_THUMB_XL, h: STATION_THUMB_XL } : undefined}
      rounded="rounded-xl"
      bordered
    />
  );
}

/**
 * 지점 행 껍데기 — 바이크(`StationRow`)·캠핑장(`CampingStationRow`)이 함께 쓴다(09-29).
 *
 * [사진 | 글 | 오른쪽 칸]. 오른쪽 칸은 **아래 끝**에 "지도" 버튼 하나(모바일·태블릿).
 * 지도가 늘 보이는 데스크톱은 버튼이 없으므로 오른쪽 칸 자체를 만들지 않는다.
 * 글 칸은 min-w-0 · 오른쪽 칸은 shrink-0 이라 긴 이름이 버튼 밑으로 파고들지 않는다.
 *
 * 행 전체가 누르는 자리(시트 열기)인데 그 안에 "지도" 버튼이 또 있으므로, 행을 <button>
 * 으로 두면 버튼 안에 버튼이 된다(잘못된 HTML). 그래서 행은 role="button" div 이고,
 * 지도 버튼은 클릭 전파를 막아 시트를 열지 않는다.
 */
export function StationRowShell({
  stationId,
  name,
  onClick,
  thumb,
  children,
}: {
  stationId: string;
  /** 지도 버튼의 스크린리더 이름("○○ 지도에서 보기")에 쓴다 */
  name: string;
  onClick: () => void;
  thumb: ReactNode;
  children: ReactNode;
}) {
  const { t, focusStation, hoverStation } = useHub();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // 안쪽 지도 버튼에서 누른 키는 그 버튼 몫이다
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={hoverStation ? () => hoverStation(stationId) : undefined}
      onMouseLeave={hoverStation ? () => hoverStation(null) : undefined}
      className="block w-full cursor-pointer text-left transition outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-inset"
    >
      <div className={cn("flex w-full items-start gap-3 py-3.5", HUB_GUTTER)}>
        {thumb}
        <div className="min-w-0 flex-1">{children}</div>
        {focusStation ? (
          <div className="flex shrink-0 flex-col items-end self-stretch">
            <ShowOnMapButton
              label={t("hub.showOnMap")}
              ariaLabel={t("hub.showOnMapAria", { name })}
              onClick={() => focusStation(stationId)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 크롤러용 링크의 클릭(10-14) — href 는 서버 HTML 에 실려 크롤러가 따라가지만, 일반 클릭은
 * 이동하지 않고(`preventDefault`) 행의 기존 동작(시트 열기)을 그대로 한다. 기존 동작이
 * 기본이라는 UX 결정 — 새 탭 열기(⌘/Ctrl/Shift/Alt 클릭)만 브라우저 기본 동작으로 보내고,
 * 그때는 행의 시트가 함께 열리지 않게 전파를 막는다. 가운데 버튼은 click 이 아니라
 * auxclick 이라 여기 오지 않는다(브라우저가 그대로 새 탭으로 연다).
 */
export function isNewTabClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}

/**
 * 지점 행의 이름 — 지점 페이지가 있으면 그 페이지의 `<a href>`, 없으면(오토바이·텐트
 * 세트가 없는 지점·반납 전용 등 404 인 지점) 지금처럼 글자만. 외형은 `className` 그대로.
 *
 * 일반 클릭은 이동하지 않고 행(`StationRowShell`)으로 올라가 시트를 연다(위 `isNewTabClick`).
 * 키보드 초점은 행 하나로 충분하므로 링크는 탭 순서에서 뺀다(tabIndex -1).
 */
export function StationNameLink({
  stationId,
  className,
  children,
}: {
  stationId: string;
  className: string;
  children: ReactNode;
}) {
  const { stationHref } = useHub();
  const href = stationHref(stationId);
  if (!href) return <span className={className}>{children}</span>;
  return (
    <a
      href={href}
      tabIndex={-1}
      onClick={(event) => {
        if (isNewTabClick(event)) event.stopPropagation();
        else event.preventDefault();
      }}
      className={className}
    >
      {children}
    </a>
  );
}

/**
 * 행 오른쪽 아래 "지도" 버튼 — 흰 바탕 · 회색 테두리 알약(h-7). 핀 아이콘은 이 버튼의
 * 뜻을 말하는 유일한 아이콘이다(장식 아님). 누르면 시트를 열지 않고 지도로 간다.
 */
function ShowOnMapButton({
  label,
  ariaLabel,
  onClick,
}: {
  label: string;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="mt-auto inline-flex h-7 items-center gap-1 rounded-full border border-gray-300 bg-white pr-2.5 pl-2 text-[12px] font-semibold whitespace-nowrap text-gray-900 transition hover:bg-gray-50 active:scale-95"
    >
      <MapPin aria-hidden className="size-3.5" />
      {label}
    </button>
  );
}

/** 얇은 배지 — 24시간 등. */
export function MetaBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 mapShow:text-xs">
      {label}
    </span>
  );
}

/** 스켈레톤 조각 — 회색 면 + 펄스. 실제 행과 같은 높이를 유지하는 것이 목적이다. */
function Bar({ className }: { className: string }) {
  return <div className={cn("animate-pulse rounded bg-gray-100", className)} />;
}

/**
 * 지점 행 스켈레톤 — 지점 상세(이름·주소·사진)가 아직 없을 때 그 자리를 잡는다.
 * 도시명("도쿄도")이나 상품 사진으로 지점을 흉내 내지 않는다.
 * `gear` — 캠핑장 행의 마지막 줄(장비 사진 원형 겹침 + "텐트 세트 N종") 자리.
 */
export function StationRowSkeleton({ gear = false }: { gear?: boolean } = {}) {
  const wide = useHubDensity() === "wide";
  return (
    <div className={cn("flex w-full items-start gap-3 py-3.5", HUB_GUTTER)}>
      {/* 실제 행(StationThumb)과 같은 크기 규칙 — 로드 순간 튀지 않게 */}
      <div
        className="shrink-0 animate-pulse rounded-xl bg-gray-100 size-(--thumb) mapShow:size-(--thumb-desk) xl:size-(--thumb-xl)"
        style={
          {
            "--thumb": `${STATION_THUMB}px`,
            "--thumb-desk": `${wide ? STATION_THUMB_DESK : STATION_THUMB}px`,
            "--thumb-xl": `${wide ? STATION_THUMB_XL : STATION_THUMB}px`,
          } as CSSProperties
        }
      />
      <div className="min-w-0 flex-1 pt-1">
        <Bar className="h-4 w-2/5" />
        <Bar className="mt-2 h-3 w-4/5" />
        <Bar className="mt-3 h-5 w-24 rounded-full" />
        {gear ? (
          <div className="mt-2 flex items-center gap-2">
            <Bar className="h-7 w-19 rounded-full" />
            <Bar className="h-4 w-20" />
          </div>
        ) : (
          <Bar className="mt-2 h-4 w-20" />
        )}
      </div>
    </div>
  );
}

/** 섹션 헤더 스켈레톤 — 도도부현 묶음도 상세가 와야 정해진다. */
export function SectionHeaderSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("py-2", HUB_GUTTER, "mapShow:pt-5 mapShow:pb-2", className)}
    >
      <Bar className="h-4 w-24" />
    </div>
  );
}

/**
 * 목록 컨테이너 클래스 — 데스크톱 목록 열(wide)은 2열 카드 그리드, 모바일 드로어·
 * 태블릿(compact)은 지금의 한 줄 목록(가로선). 지도가 오른쪽에 붙는 폭에서는 한 줄에
 * 항목 하나만 두면 여백이 너무 비어 보인다(09-23 A안).
 *
 * 2열에서 섹션 헤더는 `className="col-span-2"` 로 두 칸을 가로지르고, 행은 `HubCell`
 * 이 감싼다 — 가로선만으로는 좌우 두 항목의 경계가 읽히지 않기 때문.
 */
export function hubListClass(
  wide: boolean,
  compactClass: string,
  wideClass = "grid grid-cols-2 items-start gap-2.5",
): string {
  return wide ? wideClass : compactClass;
}

/** 2열 카드 껍데기 — compact 에서는 아무것도 감싸지 않고 그대로 통과시킨다. */
export function HubCell({
  wide,
  bare = false,
  children,
}: {
  wide: boolean;
  /** 테두리 없는 카드(지점 목록, 09-29) — 선 대신 여백으로 구분하고 hover 면만 둥글게 */
  bare?: boolean;
  children: ReactNode;
}) {
  if (!wide) return <>{children}</>;
  if (bare) {
    return <div className="overflow-hidden rounded-xl *:px-3">{children}</div>;
  }
  return (
    // 안쪽 여백은 카드가 아니라 **직계 자식(행 버튼·링크)**이 갖는다 — 카드에 px 를 주면
    // 행의 hover 회색이 여백 안쪽에서 끝나 좌우에 흰 띠가 남는다(09-23).
    <div className="overflow-hidden rounded-xl border border-gray-200 transition hover:border-gray-300 *:px-3">
      {children}
    </div>
  );
}
